import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Wallet, ListPlus, Target, Check, ArrowLeft, PartyPopper, AlarmClock, CalendarClock, Lock, Users, TrendingUp, ChevronRight, Sparkles, CreditCard,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { useBloqueo } from '../context/BloqueoContext';
import { useLimite } from '../hooks/useLimite';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';
import { Categoria, Cuenta } from '../types';
import { aNumero, AVISO_IMPORTE } from '../utils/numeros';
import {
  CATEGORIAS_PLANTILLA, CATEGORIAS_BASICAS, CategoriaPlantilla, claveCategoria,
} from '../config/categoriasPlantilla';
import { esApp } from '../utils/plataforma';
import { VENTAJAS_PLUS } from '../config/planes';
import { NOMBRE_APP } from '../config/marca';
import { DIAS_INACTIVIDAD, guardarAjustes, leerAjustes } from '../utils/recordatoriosGastos';
import { vibrar } from '../utils/hapticos';

/** Lo que se puede elegir como primera cuenta: el tipo real y cómo se llama de partida. */
const OPCIONES_CUENTA: { id: string; titulo: string; detalle: string; tipo: Cuenta['tipo']; icono: string; nombre: string; esCredito?: boolean }[] = [
  { id: 'banco',    titulo: 'Cuenta del banco', detalle: 'Donde cobras y pagas', tipo: 'corriente', icono: '🏦', nombre: 'Cuenta corriente' },
  { id: 'efectivo', titulo: 'Efectivo',          detalle: 'Lo que llevas en la cartera', tipo: 'corriente', icono: '💶', nombre: 'Efectivo' },
  { id: 'ahorro',   titulo: 'Ahorro',            detalle: 'Dinero que guardas aparte', tipo: 'ahorro', icono: '💰', nombre: 'Ahorro' },
  { id: 'tarjeta',  titulo: 'Tarjeta de crédito', detalle: 'Pagas ahora, se carga después', tipo: 'tarjeta', icono: '💳', nombre: 'Tarjeta', esCredito: true },
];

/** Importe con signo: una tarjeta de crédito puede empezar en negativo. */
const PATRON_SALDO = '-?[0-9]*[.,]?[0-9]*';
const CAMPO_IMPORTE = 'w-28 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold text-right focus:outline-none focus:ring-2 focus:ring-blue-500';

type Paso = 'hola' | 'cuenta' | 'categorias' | 'fijos' | 'presupuesto' | 'recordatorios' | 'listo';

/** Ingresos y gastos fijos que se proponen, con la categoría de la plantilla en la que caen. */
const FIJOS: { id: string; titulo: string; tipo: 'ingreso' | 'gasto'; categoria: string; dia: number }[] = [
  { id: 'nomina',   titulo: 'Nómina',              tipo: 'ingreso', categoria: 'Nómina',              dia: 28 },
  { id: 'vivienda', titulo: 'Alquiler o hipoteca', tipo: 'gasto',   categoria: 'Vivienda',            dia: 1 },
  { id: 'luz',      titulo: 'Luz y agua',          tipo: 'gasto',   categoria: 'Suministros',         dia: 5 },
  { id: 'movil',    titulo: 'Móvil e internet',    tipo: 'gasto',   categoria: 'Internet y teléfono', dia: 10 },
];

/** Categorías que se proponen para el primer presupuesto, por orden de preferencia. */
const PARA_PRESUPUESTO = ['Supermercado', 'Ocio', 'Restaurantes', 'Transporte'];

/** Días que se pueden elegir: hasta el 28, que existe en todos los meses. */
const DIAS_MES = Array.from({ length: 28 }, (_, i) => i + 1);

/** La próxima vez que llega el día `dia` del mes: este mes si aún no ha pasado. */
function proximoDiaDelMes(dia: number): Date {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), dia);
  if (fecha < hoy) fecha.setMonth(fecha.getMonth() + 1);
  return fecha;
}

interface Props {
  /** Cerrar sin terminar: se puede volver a abrir desde el resumen. */
  onClose: () => void;
  /** Terminado. Con `apuntar`, el usuario quiere apuntar ya su primer movimiento. */
  onTerminar: (apuntar: boolean) => void;
}

/**
 * Primeros pasos en Finanzas: explica en tres líneas cómo funciona y crea lo
 * imprescindible para apuntar un gasto (una cuenta y, si no hay, categorías).
 * Sin cuenta no se puede guardar ningún movimiento, y eso no se veía venir.
 * También se abre a mano desde la Ayuda: si ya hay cuentas, ese paso se salta
 * y los fijos van a la cuenta principal.
 */
export const AsistenteFinanzas: React.FC<Props> = ({ onClose, onTerminar }) => {
  useCerrarConEscape(onClose);
  const navigate = useNavigate();
  const { crearCuenta, crearCategorias, categorias, cuentas, crearTransaccionRecurrente, crearPresupuesto } = useData();
  const { marcarPlantillaCategoriasVista, tieneModulo, esPlus } = usePerfil();
  const { ofrecerSiEsLimite, ofrecerPlus } = usePlus();
  const limiteCategorias = useLimite('categorias');
  const limitePeriodicos = useLimite('periodicos');
  const limitePresupuestos = useLimite('presupuestos');
  const bloqueo = useBloqueo();

  // Si ya tiene cuentas o categorías, esos pasos sobran
  const [conCuenta] = useState(() => cuentas.length === 0);
  const [conCategorias] = useState(() => categorias.length === 0);
  // Los recordatorios son notificaciones del iPhone: en la web no se ofrecen
  const pasos: Paso[] = [
    'hola',
    ...(conCuenta ? ['cuenta' as const] : []),
    ...(conCategorias ? ['categorias' as const] : []),
    'fijos', 'presupuesto',
    ...(esApp ? ['recordatorios' as const] : []),
    'listo',
  ];
  const [paso, setPaso] = useState<Paso>('hola');
  const indice = pasos.indexOf(paso);

  // Terminar se nota en la mano (en la app)
  useEffect(() => { if (paso === 'listo') vibrar.exito(); }, [paso]);

  // ── Paso «cuenta» ─────────────────────────────────────────────────────────
  const [opcion, setOpcion] = useState(OPCIONES_CUENTA[0]);
  const [nombre, setNombre] = useState(OPCIONES_CUENTA[0].nombre);
  const [nombreTocado, setNombreTocado] = useState(false);
  const [saldo, setSaldo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const elegir = (o: typeof OPCIONES_CUENTA[number]) => {
    setOpcion(o);
    // El nombre sigue al tipo hasta que el usuario escribe el suyo
    if (!nombreTocado) setNombre(o.nombre);
  };

  const crearLaCuenta = async (e: React.FormEvent) => {
    e.preventDefault();
    const importe = saldo.trim() === '' ? 0 : aNumero(saldo);
    if (!nombre.trim()) { setError('Ponle un nombre a la cuenta'); return; }
    if (Number.isNaN(importe)) { setError(AVISO_IMPORTE); return; }
    setError('');
    setGuardando(true);
    try {
      await crearCuenta({
        nombre: nombre.trim(),
        tipo: opcion.tipo,
        icono: opcion.icono,
        saldo: importe,
        ocultarSaldo: false,
        esCredito: !!opcion.esCredito,
      });
      setPaso(pasos[indice + 1]);
    } catch (err: any) {
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'No se pudo crear la cuenta');
    } finally {
      setGuardando(false);
    }
  };

  // ── Paso «categorías» ─────────────────────────────────────────────────────
  const basicas = useMemo(() => CATEGORIAS_PLANTILLA.filter(c => CATEGORIAS_BASICAS.has(c.nombre)), []);
  const [marcadas, setMarcadas] = useState<Set<string>>(
    () => new Set(basicas.slice(0, limiteCategorias.quedan).map(claveCategoria)));
  const seleccion = basicas.filter(c => marcadas.has(claveCategoria(c)));

  const alternar = (c: CategoriaPlantilla) => setMarcadas(prev => {
    const s = new Set(prev);
    const k = claveCategoria(c);
    if (s.has(k)) s.delete(k);
    else if (s.size < limiteCategorias.quedan) s.add(k);
    return s;
  });

  const terminarCategorias = async (crear: boolean) => {
    setError('');
    if (crear && seleccion.length > 0) {
      setGuardando(true);
      try {
        await crearCategorias(seleccion);
      } catch (err: any) {
        setGuardando(false);
        if (ofrecerSiEsLimite(err)) { onClose(); return; }
        setError(err.message || 'No se pudieron crear las categorías');
        return;
      }
      setGuardando(false);
    }
    // Ya se le han ofrecido: que el resumen no vuelva a sacar la plantilla
    marcarPlantillaCategoriasVista().catch(e => console.error('No se pudo anotar la plantilla como vista', e));
    setPaso(pasos[indice + 1]);
  };

  // Las categorías se buscan por nombre: las del paso anterior acaban de crearse
  const categoriaLlamada = (tipo: Categoria['tipo'], nombreCat: string) =>
    categorias.find(c => claveCategoria(c) === claveCategoria({ tipo, nombre: nombreCat }));
  // La cuenta recién creada (si hay varias con ese nombre, la más nueva). Si ya
  // tenía cuentas, la principal: la corriente más antigua que no sea tarjeta.
  const cuentaCreada = useMemo(() => {
    if (!conCuenta) {
      const porAntiguedad = [...cuentas].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return porAntiguedad.find(c => c.tipo === 'corriente' && !c.esCredito) ?? porAntiguedad[0];
    }
    return cuentas
      .filter(c => c.nombre === nombre.trim())
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [cuentas, nombre, conCuenta]);
  const nombreCuenta = cuentaCreada?.nombre ?? nombre.trim();

  // ── Paso «fijos» ──────────────────────────────────────────────────────────
  const fijosPosibles = FIJOS.filter(f => categoriaLlamada(f.tipo, f.categoria));
  const [importesFijos, setImportesFijos] = useState<Record<string, string>>({});
  const [diasFijos, setDiasFijos] = useState<Record<string, number>>(
    () => Object.fromEntries(FIJOS.map(f => [f.id, f.dia])),
  );

  const guardarFijos = async () => {
    setError('');
    const rellenos = fijosPosibles.filter(f => (importesFijos[f.id] ?? '').trim() !== '');
    if (rellenos.some(f => !(aNumero(importesFijos[f.id]) > 0))) { setError(AVISO_IMPORTE); return; }
    if (rellenos.length > 0 && !cuentaCreada) { setError('No encuentro la cuenta que has creado. Vuelve a intentarlo en un momento.'); return; }
    if (rellenos.length > limitePeriodicos.quedan) {
      setError(`En tu plan caben ${limitePeriodicos.quedan} movimientos periódicos más: deja en blanco alguno.`);
      return;
    }
    setGuardando(true);
    try {
      for (const f of rellenos) {
        await crearTransaccionRecurrente({
          nombre: f.titulo,
          tipo: f.tipo,
          cantidad: aNumero(importesFijos[f.id]),
          categoriaId: categoriaLlamada(f.tipo, f.categoria)!.id,
          cuentaId: cuentaCreada!.id,
          descripcion: '',
          periodicidad: 'mensual',
          proximaFecha: proximoDiaDelMes(diasFijos[f.id]),
          activo: true,
        });
      }
    } catch (err: any) {
      setGuardando(false);
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'No se pudieron guardar los fijos');
      return;
    }
    setGuardando(false);
    setPaso(pasos[indice + 1]);
  };

  // ── Paso «presupuesto» ────────────────────────────────────────────────────
  const paraPresupuesto = PARA_PRESUPUESTO
    .map(n => categoriaLlamada('gasto', n))
    .filter((c): c is Categoria => !!c)
    .slice(0, 2);
  const [importesPresupuesto, setImportesPresupuesto] = useState<Record<string, string>>({});

  const guardarPresupuestos = async () => {
    setError('');
    const rellenos = paraPresupuesto.filter(c => (importesPresupuesto[c.id] ?? '').trim() !== '');
    if (rellenos.some(c => !(aNumero(importesPresupuesto[c.id]) > 0))) { setError(AVISO_IMPORTE); return; }
    if (rellenos.length > limitePresupuestos.quedan) {
      setError(`En tu plan caben ${limitePresupuestos.quedan} presupuestos más: deja en blanco alguno.`);
      return;
    }
    const hoy = new Date();
    const mes = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
    setGuardando(true);
    try {
      for (const c of rellenos) {
        await crearPresupuesto({
          nombre: c.nombre,
          limite: aNumero(importesPresupuesto[c.id]),
          categoriaId: c.id,
          mes,
          periodicidad: 'mensual',
          repetitivo: true,
          gastado: 0,
        });
      }
    } catch (err: any) {
      setGuardando(false);
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'No se pudieron crear los presupuestos');
      return;
    }
    setGuardando(false);
    setPaso(pasos[indice + 1]);
  };

  /** Lo que se ofrece al final, fuera de Finanzas o fuera del día a día. */
  const otrasCosas: { Icono: React.ElementType; titulo: string; texto: string; ruta: string }[] = [
    ...(bloqueo.disponible && !bloqueo.activo
      ? [{ Icono: Lock, titulo: `Bloquear con ${bloqueo.nombreBiometria}`, texto: 'Que nadie vea tus cuentas si te cogen el móvil. En Configuración → Perfil.', ruta: '/configuracion' }]
      : []),
    ...(tieneModulo('amedias') ? [{ Icono: Users, titulo: 'Gastos compartidos (A medias)', texto: 'Viajes, piso o cenas: quién ha pagado qué y quién debe.', ruta: '/amedias' }] : []),
    ...(esApp ? [{ Icono: CreditCard, titulo: 'Apuntar al pagar con Apple Pay', texto: 'El gasto se abre solo al pagar, con la cuenta de tu tarjeta.', ruta: '/finanzas/dashboard?applepay=1' }] : []),
    { Icono: TrendingUp, titulo: 'Inversiones', texto: 'Fondos, acciones o depósitos, y cómo van.', ruta: '/finanzas/inversiones' },
  ];

  /** Al terminar, ir a otra parte de la app. */
  const irA = (ruta: string) => { onTerminar(false); navigate(ruta); };

  // ── Paso «recordatorios» ──────────────────────────────────────────────────
  const [avisoInactividad, setAvisoInactividad] = useState(true);
  const [dias, setDias] = useState(3);
  const [diario, setDiario] = useState(false);
  const [hora, setHora] = useState('21:00');
  const [sinPermiso, setSinPermiso] = useState(false);

  const terminarRecordatorios = async (activar: boolean) => {
    setSinPermiso(false);
    if (activar && (avisoInactividad || diario)) {
      setGuardando(true);
      try {
        const ajustes = { ...(await leerAjustes()), inactividad: avisoInactividad, dias, diario, hora };
        if (!(await guardarAjustes(ajustes))) { setSinPermiso(true); return; }
      } catch (e) {
        console.error('No se pudieron guardar los recordatorios', e);
      } finally {
        setGuardando(false);
      }
    }
    setPaso('listo');
  };

  // ── Piezas ────────────────────────────────────────────────────────────────
  const botonPrincipal = 'w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-semibold transition disabled:opacity-50';
  const botonSecundario = 'w-full py-3 rounded-xl text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition';

  const fila = (Icono: React.ElementType, color: string, titulo: string, texto: string) => (
    <li className="flex gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
        <Icono className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-gray-900 dark:text-white">{titulo}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{texto}</p>
      </div>
    </li>
  );

  const grupoCategorias = (tipo: CategoriaPlantilla['tipo'], titulo: string) => (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{titulo}</h3>
      <div className="flex flex-wrap gap-2">
        {basicas.filter(c => c.tipo === tipo).map(c => {
          const marcada = marcadas.has(claveCategoria(c));
          return (
            <button key={claveCategoria(c)} type="button" onClick={() => alternar(c)} aria-pressed={marcada}
              className={`inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-full border text-sm transition ${
                marcada
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
              <span>{c.icono}</span>
              <span>{c.nombre}</span>
              {marcada && <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
            </button>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center capa-hoja">
      <div role="dialog" aria-modal="true" aria-labelledby="asistente-titulo"
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl
                   max-h-full flex flex-col shadow-2xl">

        {/* Cabecera: atrás, puntos de avance y cerrar */}
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
          <div className="w-11">
            {indice > 0 && paso !== 'listo' && (
              <button type="button" onClick={() => { setError(''); setPaso(pasos[indice - 1]); }}
                aria-label="Paso anterior"
                className="boton-cerrar text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100
                           hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="flex gap-1.5" aria-label={`Paso ${indice + 1} de ${pasos.length}`}>
            {pasos.map((p, i) => (
              <span key={p} className={`h-1.5 rounded-full transition-all ${
                i === indice ? 'w-6 bg-blue-600' : i < indice ? 'w-1.5 bg-blue-300 dark:bg-blue-700' : 'w-1.5 bg-gray-200 dark:bg-gray-700'
              }`} />
            ))}
          </div>
          <div className="w-11 flex justify-end">
            <button type="button" onClick={onClose} aria-label="Cerrar"
              className="boton-cerrar text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                         hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 pb-5 overflow-y-auto">

          {paso === 'hola' && (
            <div className="space-y-6">
              <div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">Vamos a preparar tus finanzas</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">Así funciona, en tres ideas:</p>
              </div>
              <ul className="space-y-4">
                {fila(Wallet, 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
                  '1. Tus cuentas', 'Dónde está tu dinero: el banco, el efectivo, una tarjeta…')}
                {fila(ListPlus, 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
                  '2. Tus gastos e ingresos', 'Cada uno se apunta en una cuenta y con una categoría. El saldo se actualiza solo.')}
                {fila(Target, 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400',
                  '3. Tus presupuestos (si quieres)', 'Un límite al mes por categoría para ver cómo vas.')}
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {conCuenta
                  ? 'Para empezar solo hace falta una cuenta. Es un minuto.'
                  : 'Ya tienes cuentas: repasamos lo demás en un minuto.'}
              </p>
              <button type="button" onClick={() => setPaso(pasos[1])} className={botonPrincipal}>
                {conCuenta ? 'Crear mi primera cuenta' : 'Empezar'}
              </button>
            </div>
          )}

          {paso === 'cuenta' && (
            <form onSubmit={crearLaCuenta} className="space-y-5">
              <div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">Tu primera cuenta</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">¿Dónde tienes el dinero que quieres controlar?</p>
              </div>

              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tipo de cuenta">
                {OPCIONES_CUENTA.map(o => {
                  const activa = o.id === opcion.id;
                  return (
                    <button key={o.id} type="button" role="radio" aria-checked={activa} onClick={() => elegir(o)}
                      className={`text-left p-3 rounded-xl border-2 transition ${
                        activa
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}>
                      <span className="text-2xl">{o.icono}</span>
                      <p className="font-semibold text-sm text-gray-900 dark:text-white mt-1">{o.titulo}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{o.detalle}</p>
                    </button>
                  );
                })}
              </div>

              <div>
                <label htmlFor="asistente-nombre" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Nombre
                </label>
                <input id="asistente-nombre" type="text" value={nombre}
                  onChange={e => { setNombre(e.target.value); setNombreTocado(true); }}
                  placeholder="Ej: Santander, Cartera…"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700
                             text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div>
                <label htmlFor="asistente-saldo" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  ¿Cuánto hay ahora? (€)
                </label>
                <input id="asistente-saldo" type="text" inputMode="decimal" pattern={PATRON_SALDO} title={AVISO_IMPORTE}
                  value={saldo} onChange={e => setSaldo(e.target.value)} placeholder="0,00"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700
                             text-gray-900 dark:text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  Mira el saldo en la app de tu banco. Si no lo sabes, déjalo en blanco: se puede cambiar
                  luego en Configuración → Cuentas.
                </p>
              </div>

              {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              <button type="submit" disabled={guardando} className={botonPrincipal}>
                {guardando ? 'Creando…' : 'Crear cuenta'}
              </button>
            </form>
          )}

          {paso === 'categorias' && (
            <div className="space-y-5">
              <div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">Tus categorías</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Sirven para saber en qué se va el dinero. Te proponemos estas; quita las que no uses.
                </p>
              </div>
              {grupoCategorias('gasto', 'Gastos')}
              {grupoCategorias('ingreso', 'Ingresos')}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Puedes añadir más, cambiarlas o borrarlas en Configuración → Categorías.
              </p>
              {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="space-y-1">
                <button type="button" onClick={() => terminarCategorias(true)} disabled={guardando || seleccion.length === 0}
                  className={botonPrincipal}>
                  {guardando ? 'Creando…' : `Usar estas ${seleccion.length} categorías`}
                </button>
                <button type="button" onClick={() => terminarCategorias(false)} disabled={guardando} className={botonSecundario}>
                  Prefiero crearlas yo
                </button>
              </div>
            </div>
          )}

          {paso === 'fijos' && (
            <div className="space-y-5">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 mb-3">
                  <CalendarClock className="w-6 h-6" />
                </div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">Lo que se repite cada mes</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Pon lo que cobras y pagas cada mes y se apuntará solo en «{nombreCuenta}». Deja en blanco lo que no tengas.
                </p>
              </div>
              {fijosPosibles.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Para esto hacen falta categorías como Nómina o Vivienda. Cuando las tengas, puedes añadir tus fijos en Finanzas → Periódicos.
                </p>
              ) : (
                <ul className="space-y-3">
                  {fijosPosibles.map(f => (
                    <li key={f.id} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <label htmlFor={`fijo-${f.id}`} className="block font-semibold text-gray-900 dark:text-white">{f.titulo}</label>
                        <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                          el día
                          <select value={diasFijos[f.id]} aria-label={`Día del mes de ${f.titulo}`}
                            onChange={e => setDiasFijos(d => ({ ...d, [f.id]: Number(e.target.value) }))}
                            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-1.5 py-0.5">
                            {DIAS_MES.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </span>
                      </div>
                      <input id={`fijo-${f.id}`} type="text" inputMode="decimal" pattern={PATRON_SALDO} title={AVISO_IMPORTE}
                        value={importesFijos[f.id] ?? ''} placeholder="0,00 €"
                        onChange={e => setImportesFijos(v => ({ ...v, [f.id]: e.target.value }))}
                        className={CAMPO_IMPORTE} />
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Si el importe cambia algún mes, lo corriges en ese movimiento. Los ves y cambias en Finanzas → Periódicos.
              </p>
              {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="space-y-1">
                {fijosPosibles.length > 0 && (
                  <button type="button" onClick={guardarFijos}
                    disabled={guardando || !fijosPosibles.some(f => (importesFijos[f.id] ?? '').trim() !== '')}
                    className={botonPrincipal}>
                    {guardando ? 'Guardando…' : 'Guardar mis fijos'}
                  </button>
                )}
                <button type="button" onClick={() => { setError(''); setPaso(pasos[indice + 1]); }} disabled={guardando}
                  className={fijosPosibles.length > 0 ? botonSecundario : botonPrincipal}>
                  {fijosPosibles.length > 0 ? 'Ahora no' : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {paso === 'presupuesto' && (
            <div className="space-y-5">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 mb-3">
                  <Target className="w-6 h-6" />
                </div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">Tu primer presupuesto</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Un límite al mes para lo que más se escapa. Verás cuánto te queda, y el resumen te avisa cuando estés cerca.
                </p>
              </div>
              {paraPresupuesto.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Para esto hace falta alguna categoría de gasto. Cuando la tengas, crea tus presupuestos en Finanzas → Presupuestos.
                </p>
              ) : (
                <ul className="space-y-3">
                  {paraPresupuesto.map(c => (
                    <li key={c.id} className="flex items-center gap-3">
                      <label htmlFor={`presupuesto-${c.id}`} className="flex-1 min-w-0 font-semibold text-gray-900 dark:text-white">
                        <span className="mr-1.5">{c.icono}</span>{c.nombre}
                        <span className="block text-sm font-normal text-gray-500 dark:text-gray-400">al mes</span>
                      </label>
                      <input id={`presupuesto-${c.id}`} type="text" inputMode="decimal" pattern={PATRON_SALDO} title={AVISO_IMPORTE}
                        value={importesPresupuesto[c.id] ?? ''} placeholder="0,00 €"
                        onChange={e => setImportesPresupuesto(v => ({ ...v, [c.id]: e.target.value }))}
                        className={CAMPO_IMPORTE} />
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Se repite cada mes. Si un mes necesitas otro importe (por ejemplo, en Navidad), cámbialo en
                Finanzas → Presupuestos con el lápiz y elige «Solo ese mes».
              </p>
              {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="space-y-1">
                {paraPresupuesto.length > 0 && (
                  <button type="button" onClick={guardarPresupuestos}
                    disabled={guardando || !paraPresupuesto.some(c => (importesPresupuesto[c.id] ?? '').trim() !== '')}
                    className={botonPrincipal}>
                    {guardando ? 'Creando…' : 'Crear presupuesto'}
                  </button>
                )}
                <button type="button" onClick={() => { setError(''); setPaso(pasos[indice + 1]); }} disabled={guardando}
                  className={paraPresupuesto.length > 0 ? botonSecundario : botonPrincipal}>
                  {paraPresupuesto.length > 0 ? 'Ahora no' : 'Continuar'}
                </button>
              </div>
            </div>
          )}

          {paso === 'recordatorios' && (
            <div className="space-y-5">
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 mb-3">
                  <AlarmClock className="w-6 h-6" />
                </div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">¿Te lo recordamos?</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Lo difícil es acordarse de apuntar. Podemos mandarte un aviso al iPhone.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={avisoInactividad} onChange={e => setAvisoInactividad(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-blue-600" />
                <span className="flex-1">
                  <span className="block font-semibold text-gray-900 dark:text-white">Si llevo días sin apuntar</span>
                  <span className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Tras
                    <select value={dias} onChange={e => setDias(Number(e.target.value))}
                      className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-2 py-1">
                      {DIAS_INACTIVIDAD.map(d => <option key={d} value={d}>{d} días</option>)}
                    </select>
                    sin entrar
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={diario} onChange={e => setDiario(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-blue-600" />
                <span className="flex-1">
                  <span className="block font-semibold text-gray-900 dark:text-white">Cada día</span>
                  <span className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                    A las
                    <input type="time" value={hora} onChange={e => e.target.value && setHora(e.target.value)}
                      className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-2 py-1" />
                    (no sale si ya has apuntado algo ese día)
                  </span>
                </span>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Puedes cambiarlo cuando quieras en Configuración → Perfil.
              </p>
              {sinPermiso && (
                <p role="alert" className="text-sm text-amber-600 dark:text-amber-400">
                  Sin permiso para notificaciones no podemos avisarte. Puedes darlo en Ajustes de iOS → Notificaciones → myarinn, o seguir sin avisos.
                </p>
              )}
              <div className="space-y-1">
                <button type="button" onClick={() => terminarRecordatorios(true)}
                  disabled={guardando || !(avisoInactividad || diario)} className={botonPrincipal}>
                  {guardando ? 'Activando…' : 'Activar avisos'}
                </button>
                <button type="button" onClick={() => terminarRecordatorios(false)} disabled={guardando} className={botonSecundario}>
                  Ahora no
                </button>
              </div>
            </div>
          )}

          {paso === 'listo' && (
            <div className="space-y-6 text-center pt-2">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                <PartyPopper className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 id="asistente-titulo" className="text-2xl font-bold text-gray-900 dark:text-white">¡Todo listo!</h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {conCuenta ? <>Ya tienes «{nombreCuenta}». </> : null}Cuando gastes o cobres algo, pulsa el botón
                  <span className="inline-flex items-center justify-center w-6 h-6 mx-1 rounded-full bg-blue-600 text-white font-bold align-middle">+</span>
                  y apúntalo.
                </p>
              </div>
              <div className="space-y-1">
                <button type="button" onClick={() => onTerminar(true)} className={botonPrincipal}>
                  {conCuenta ? 'Apuntar mi primer gasto' : 'Apuntar un gasto'}
                </button>
                <button type="button" onClick={() => onTerminar(false)} className={botonSecundario}>
                  Ir al resumen
                </button>
              </div>
              {otrasCosas.length > 0 && (
                <div className="text-left border-t border-gray-100 dark:border-gray-700 pt-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Más cosas que puedes hacer
                  </h3>
                  <ul>
                    {otrasCosas.map(o => (
                      <li key={o.ruta + o.titulo}>
                        <button type="button" onClick={() => irA(o.ruta)}
                          className="w-full flex items-center gap-3 py-2.5 min-h-[44px] text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                          <o.Icono className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold text-gray-900 dark:text-white">{o.titulo}</span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">{o.texto}</span>
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Solo con los límites activados y sin Plus: antes todo es gratis y no hay nada que ofrecer */}
              {!esPlus && (
                <div className="text-left rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-amber-900 dark:text-amber-200">
                    <Sparkles className="w-4 h-4" /> {NOMBRE_APP} Plus
                  </h3>
                  <ul className="mt-2 space-y-1">
                    {VENTAJAS_PLUS.slice(0, 4).map(v => (
                      <li key={v} className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200">
                        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {v}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
                    No hace falta ahora: con el plan gratis tienes todo lo básico, y puedes hacerte Plus cuando
                    quieras en Configuración → Perfil.
                  </p>
                  <button type="button" onClick={() => ofrecerPlus()}
                    className="mt-3 text-xs font-semibold px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition">
                    Ver {NOMBRE_APP} Plus
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
